import React, { useCallback, useEffect, useRef, useState } from 'react';

import Modal from './Modal';
import Button from './Button';

// Themed confirmation dialog built on Modal.
//
// Imperative usage via the useConfirm() hook — mirrors window.confirm but
// returns a Promise<boolean> and renders inside our themed Modal:
//
//   const { confirm, dialog } = useConfirm();
//   const ok = await confirm({ title, message, confirmLabel, danger: true });
//   ...
//   return (<div>{content}{dialog}</div>);
//
// Pass `confirmText` for an irreversible action to require the user to type
// that exact string before Confirm enables (e.g. confirmText: cat.name).

export function ConfirmDialog({
  isOpen,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  confirmText,
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState('');

  // reset the typed value each time the dialog opens, so a stale match from
  // a previous confirmation doesn't silently pre-enable this one
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  const locked = !!confirmText;
  const canConfirm = !locked || typed === confirmText;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} dismissOnBackdropClick={false}>
      {message && <p style={{ marginBottom: 'var(--ss-space-4)' }}>{message}</p>}
      {locked && (
        <div className="ss-field" style={{ marginBottom: 'var(--ss-space-4)' }}>
          <label className="ss-label" htmlFor="ss-confirm-dialog-input">
            Type <strong>{confirmText}</strong> to confirm
          </label>
          <input
            id="ss-confirm-dialog-input"
            className="ss-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>
      )}
      <div className="ss-form__actions">
        <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={!canConfirm}>{confirmLabel}</Button>
        <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
      </div>
    </Modal>
  );
}

export function useConfirm() {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({ open: true, opts });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  const dialog = (
    <ConfirmDialog
      isOpen={state.open}
      {...state.opts}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, dialog };
}

export default ConfirmDialog;
